import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {CaretDown, Plus} from '@phosphor-icons/react';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL} from '../../channels/channels';

export function PlatformsBox({clientId, clientAdAccounts, clientStores, channels, ignoredChannels,
                              onConnectChannel, onUnignoreChannel, readOnly}) {
    const {t} = useTranslation();
    const [connectListOpen, setConnectListOpen] = useState(false);

    if (!channels || channels.length === 0) return null;

    // Build map of accounts by channel
    const accountsByChannel = {};
    (clientAdAccounts || []).forEach(acc => {
        const ch = acc.channel;
        if (!accountsByChannel[ch]) accountsByChannel[ch] = [];
        accountsByChannel[ch].push(acc);
    });

    (clientStores || []).forEach(store => {
        const ch = store.channel;
        if (!accountsByChannel[ch]) accountsByChannel[ch] = [];
        accountsByChannel[ch].push(store);
    });

    // Build ignored channels map
    const ignoredMap = {};
    (ignoredChannels || []).forEach(ic => {
        ignoredMap[ic.channel] = ic;
    });

    // Only show channels the agency has enabled
    const visibleChannels = channels.filter(ch => CHANNEL_INFO_BY_CHANNEL[ch]);

    // Classify into three groups
    const connected = [];
    const unconnected = [];
    const ignored = [];

    visibleChannels.forEach(ch => {
        const hasAccounts = accountsByChannel[ch] && accountsByChannel[ch].length > 0;
        const isIgnored = !!ignoredMap[ch];

        if (hasAccounts) {
            connected.push(ch);
        } else if (isIgnored) {
            ignored.push(ch);
        } else {
            unconnected.push(ch);
        }
    });

    const connectableChannels = [...unconnected, ...ignored];

    const formatAccountInfo = (ch) => {
        const accounts = accountsByChannel[ch] || [];
        if (accounts.length === 1) {
            const acc = accounts[0];
            const name = acc.name || '';
            const platformId = acc.account_id || '';
            return name ? `${name} (ID: ${platformId})` : `ID: ${platformId}`;
        }
        const names = accounts.map(a => a.name || `ID: ${a.account_id}`).join('\n');
        return {
            text: t('control_central.platforms_accounts_count', {count: accounts.length}),
            title: names
        };
    };

    const formatIgnoredInfo = (ch) => {
        const ic = ignoredMap[ch];
        if (!ic) return null;
        if (ic.ignored_by_name && ic.ignored_at) {
            const date = new Date(ic.ignored_at);
            const formatted = date.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit'});
            return t('control_central.platforms_ignored_by', {user: ic.ignored_by_name, date: formatted, interpolation: {escapeValue: false}});
        }
        return t('control_central.platforms_ignored');
    };

    return (
        <div className="box platforms-box">
            <div className="platforms-header">
                <h3 className="platforms-title">{t('control_central.platforms_title')}</h3>
                {!readOnly && (
                    <Link className="cc-action-button -small" to={`/client/${clientId}/conections`}>
                        {t('control_central.platforms_manage')} &rarr;
                    </Link>
                )}
            </div>
            <div className="platforms-list">
                {connected.map(channel => {
                    const info = CHANNEL_INFO_BY_CHANNEL[channel];
                    const accountInfo = formatAccountInfo(channel);
                    const isMultiple = typeof accountInfo === 'object';

                    return (
                        <div key={channel} className="platforms-row">
                            <div className="platforms-channel">
                                <ChannelLogo channel={channel} height={18} width={18} hspace={0} vspace={0}/>
                                <span className="platforms-channel-name">{info.Spanish}</span>
                            </div>
                            <span className="platforms-account-info" title={isMultiple ? accountInfo.title : undefined}>
                                {isMultiple ? accountInfo.text : accountInfo}
                            </span>
                        </div>
                    );
                })}

                {connected.length === 0 && (
                    <div className="cc-empty-state -compact">
                        <p>{t('control_central.platforms_no_connected')}</p>
                    </div>
                )}
            </div>

            {!readOnly && connectableChannels.length > 0 && (
                <div className="platforms-connect-block">
                    <button
                        type="button"
                        className="cc-action-button -primary platforms-connect-new-btn"
                        onClick={() => setConnectListOpen((open) => !open)}
                        aria-expanded={connectListOpen}
                    >
                        <Plus size={14} weight="bold"/>
                        {t('control_central.platforms_connect_new')}
                        <CaretDown size={14} weight="bold"/>
                    </button>

                    {connectListOpen && (
                        <div className="platforms-connect-list">
                            {connectableChannels.map(channel => {
                                const info = CHANNEL_INFO_BY_CHANNEL[channel];
                                const ignoredInfo = formatIgnoredInfo(channel);
                                const isIgnored = ignored.includes(channel);

                                return (
                                    <div key={channel} className={`platforms-row${isIgnored ? ' -ignored' : ''}`}>
                                        <div className="platforms-channel">
                                            <ChannelLogo channel={channel} height={18} width={18} hspace={0} vspace={0}/>
                                            <span className="platforms-channel-name">{info.Spanish}</span>
                                            {ignoredInfo && <span className="platforms-ignored-info">{ignoredInfo}</span>}
                                        </div>
                                        <button
                                            className="platforms-badge-connect"
                                            onClick={() => isIgnored ? onUnignoreChannel(channel) : onConnectChannel(channel)}
                                        >
                                            {t('control_central.platforms_connect')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
